/**
 * Axios request interceptor for OpenTelemetry trace context propagation
 *
 * Injects W3C traceparent header into all outgoing HTTP requests.
 */

import {InternalAxiosRequestConfig} from 'axios';

import {getLoggerCallback, telemetryConfig} from './config';
import {tracer} from './tracer';

/**
 * OpenTelemetry propagation interceptor for Axios
 *
 * Automatically injects traceparent (and optionally tracestate) headers
 * into all outgoing requests. If no trace context is active, creates one
 * automatically for the request.
 *
 * This interceptor:
 * - Never throws errors (fail-safe design)
 * - Automatically creates traces for requests if none exists
 * - Respects the enabled configuration flag
 * - Logs debug information when debug mode is enabled
 *
 * Usage:
 * ```typescript
 * axiosInstance.interceptors.request.use(otelPropagationInterceptor);
 * ```
 *
 * @param config - Axios request configuration
 * @returns Modified request configuration with OTEL headers
 */
export function otelPropagationInterceptor(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  try {
    // Skip if telemetry is disabled
    if (!telemetryConfig.enabled) {
      return config;
    }

    // Skip if tracer is not enabled
    if (!tracer.isEnabled()) {
      return config;
    }

    // Get or create trace context
    let traceparent = tracer.getTraceparent();

    // If no active trace exists, create one for this request
    if (!traceparent) {
      const url = config.url || 'unknown';
      const method = config.method || 'unknown';
      tracer.startTrace(`http_${method}_${url}`);
      traceparent = tracer.getTraceparent();
    }

    // Inject header if we have a trace (should always be true now)
    if (traceparent) {
      // Inject W3C traceparent header using set method
      config.headers.set('traceparent', traceparent);

      // Optionally inject tracestate header (for vendor-specific data)
      const tracestate = tracer.getTracestate();
      if (tracestate) {
        config.headers.set('tracestate', tracestate);
      }

      if (telemetryConfig.debug) {
        const callback = getLoggerCallback();
        if (callback) {
          callback('OTEL Interceptor', 'Injected trace headers', {
            timestamp: Date.now(),
            level: 'debug',
            context: {
              url: config.url,
              method: config.method,
              traceparent,
              tracestate: tracestate || 'none',
            },
          });
        }
      }
    }
  } catch (error) {
    // Never throw - telemetry failures should not break API calls
    if (telemetryConfig.debug || __DEV__) {
      const callback = getLoggerCallback();
      if (callback) {
        callback('OTEL Interceptor', 'Failed to inject trace headers', {
          timestamp: Date.now(),
          level: 'warn',
          context: {error},
        });
      }
    }
    // Continue with original config
  }

  return config;
}
